import { useEffect } from 'react';

/**
 * Set per-route document.title, meta description, canonical URL, and
 * og:title / og:description / og:url. Cleans up on unmount so route changes
 * never leave stale metadata behind.
 *
 * This is the SPA workaround for the Ahrefs "duplicate pages" problem:
 * every route serves the same index.html, so unique metadata must be
 * injected client-side by each page. JS-enabled crawlers (Google, Bing,
 * Ahrefs' newer renderer) execute it before capturing the DOM.
 */
export default function usePageMeta({ title, description, path, noindex = false }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      return { el, prev };
    };

    const canonicalHref = `https://finalframe.site${path}`;
    const tags = [
      setMeta('description', description),
      setMeta('og:title', title, true),
      setMeta('og:description', description, true),
      setMeta('og:url', canonicalHref, true),
      setMeta('og:image', 'https://finalframe.site/og-image.png', true),
      setMeta('twitter:title', title),
      setMeta('twitter:description', description),
      setMeta('twitter:image', 'https://finalframe.site/og-image.png'),
      setMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow'),
    ];

    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute('href');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalHref);

    return () => {
      document.title = prevTitle;
      tags.forEach(({ el, prev }) => {
        if (prev == null) el.remove();
        else el.setAttribute('content', prev);
      });
      if (prevCanonical) canonical.setAttribute('href', prevCanonical);
      else canonical.remove();
    };
  }, [title, description, path, noindex]);
}
